// ThingsToDo.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// === HERO BACKGROUND ===
import heroBg from '../assets/pick/1.jpg';

// === ACTIVITY IMAGES ===
import ayurveda from '../assets/ThingsToDo/Ayurvedic-Therapies-For-Stress-Relief.webp';
import diving from '../assets/ThingsToDo/unadiving1.jpg';
import elephantRide from '../assets/ThingsToDo/elephant-back-riding.jpg';
import fishing from '../assets/ThingsToDo/S4.jpg';
import ballooning from '../assets/ThingsToDo/hot-air-baloon.jpg';
import jeepSafari from '../assets/ThingsToDo/2409696_orig.jpg';
import whiteWaterRafting from '../assets/ThingsToDo/rafting7.jpg';
import riverSafari from '../assets/ThingsToDo/river-safari-slider1.jpg';
import snorkeling from '../assets/ThingsToDo/Snorkelers_2.jpg';
import teaTasting from '../assets/ThingsToDo/Tea-Tasting.avif';
import templeVisit from '../assets/ThingsToDo/Buddhist-Temples-in-Sri-Lanka.jpg';
import wildlifeTour from '../assets/ThingsToDo/protect-the-environment-on-your-sri-lanka-wildlife-safari-header.jpg';

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const ThingsToDo = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // === DATA ARRAY ===
    const activities = [
        { image: ayurveda, title: t('thingsToDoPage.ayurveda.title'), route: '/ayurveda' },
        { image: diving, title: t('thingsToDoPage.diving.title'), route: '/diving' },
        { image: elephantRide, title: t('thingsToDoPage.elephantRide.title'), route: '/elephant-ride' },
        { image: fishing, title: t('thingsToDoPage.fishing.title'), route: '/fishing' },
        { image: ballooning, title: t('thingsToDoPage.ballooning.title'), route: '/ballooning' },
        { image: jeepSafari, title: t('thingsToDoPage.jeepSafari.title'), route: '/jeep-safari' },
        { image: whiteWaterRafting, title: t('thingsToDoPage.whiteWaterRafting.title'), route: '/white-water-rafting' },
        { image: riverSafari, title: t('thingsToDoPage.riverSafari.title'), route: '/river-safari' },
        { image: snorkeling, title: t('thingsToDoPage.snorkeling.title'), route: '/snorkeling' },
        { image: teaTasting, title: t('thingsToDoPage.teaTasting.title'), route: '/tea-tasting' },
        { image: templeVisit, title: t('thingsToDoPage.templeVisit.title'), route: '/temple-visit' },
        { image: wildlifeTour, title: t('thingsToDoPage.wildlifeTour.title'), route: '/wildlife-safari' },
    ];

    return (
        <div className="bg-black">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh]"
            >
                <img
                    src={heroBg}
                    alt={t('thingsToDoPage.heroAlt')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-[100px]">
                    <div className="w-full">
                        <h1 className="text-center text-[50px] font-semibold leading-[80px] text-white font-copperplate md:text-[60px] lg:text-[80px]">
                            {t('thingsToDoPage.pageTitle')}
                        </h1>
                        <div className="flex justify-center text-center leading-8">
                            <p className="mt-6 max-w-[90%] font-lato text-[16px] font-semibold tracking-[5%] text-[#96BA33] sm:mt-10 sm:max-w-2xl sm:text-[25px]">
                                {t('thingsToDoPage.subtitle')}
                            </p>
                        </div>

                        <div className="absolute left-[30px] top-10">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-10 right-[30px]">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ===== ACTIVITIES SECTION – NEW UI WITH "VIEW DETAILS" BUTTON ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-12"
            >
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <h2 className="font-konkhmer text-4xl font-bold uppercase md:text-5xl lg:text-6xl">
                            <span
                                style={{
                                    background: 'linear-gradient(to right, #009CE6, #96BA33)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('thingsToDoPage.sectionTitle')}
                            </span>
                        </h2>
                        <div className="mx-auto mt-3 h-[3px] w-24 bg-[#009CE6] lg:w-32"></div>
                        <div className="mx-auto mt-1 h-[3px] w-16 bg-[#96BA33] lg:w-24"></div>
                    </div>

                    <div className="mt-12 flex w-full items-stretch gap-4 overflow-x-auto px-4 pb-5 md:grid md:grid-cols-4 md:pb-0 lg:mt-16 lg:gap-5 lg:px-5">
                        {activities.map((item, idx) => (
                            <motion.div
                                key={idx}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="relative h-[620px] w-[330px] shrink-0 overflow-clip rounded-[15px] md:h-[290px] md:w-auto lg:h-[480px] xl:h-[650px] cursor-pointer"
                                onClick={() => navigate(item.route)}
                            >
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute bottom-0 flex w-full flex-col items-center bg-gradient-to-t from-black/80 to-transparent px-3 py-6 md:py-4 lg:py-7">
                                    <h4 className="text-center font-redHatDisplay text-2xl font-bold text-white md:text-sm lg:text-xl xl:text-2xl">
                                        {item.title}
                                    </h4>

                                    {/* VIEW DETAILS BUTTON */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(item.route);
                                        }}
                                        className="mt-4 bg-[#009CE6] text-white px-6 py-2 rounded-full font-bold text-sm hover:bg-[#96BA33] transition-colors shadow-lg"
                                    >
                                        {t('thingsToDoPage.viewMore')}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default ThingsToDo;